/**
 * 예약(EML) 발송 경로의 Reply-To 헤더(account.replyTo) 생성 로직.
 *
 * EML 경로는 JSON 발송과 달리 서버 스키마 검증(422)을 거치지 않으므로, malformed 값이
 * 원문 헤더를 오염(헤더 인젝션)시키지 않도록 클라이언트에서 방어한다. 순수 함수로 분리해
 * 단위 테스트한다.
 */

import { base64utf8String } from 'libs/eml-format'

import type { EmlHeader } from './threadingHeaders'

export interface ReplyToAddress {
  email?: string
  name?: string
}

/** CR/LF·제어문자가 섞이면 헤더 인젝션 위험 → 해당 주소를 제외한다. */
const isSafeHeaderText = (value: string): boolean => !/[\x00-\x1f\x7f]/.test(value)

/**
 * account.replyTo(주소 객체 배열)로 EML Reply-To 헤더 배열을 만든다.
 * - 값이 없거나 모두 무효하면 빈 배열을 반환해 헤더를 생략한다(회귀 방지).
 * - account.replyTo 는 단일 이메일 문자열에서 온 `{ email, name: '' }` 형태이므로 통상 email 만 실린다.
 * - name 이 있으면 RFC 2047 encoded-word(base64) 로 인코딩해 비ASCII 를 안전하게 담는다.
 */
export const buildReplyToHeaders = (replyTo?: ReplyToAddress[] | null): EmlHeader[] => {
  if (replyTo == null || replyTo.length === 0) {
    return []
  }

  const value = replyTo
    .map((item) => {
      const email = item?.email?.trim()
      if (email == null || email.length === 0 || !isSafeHeaderText(email)) {
        return null
      }
      const name = item?.name?.trim()
      if (name != null && name.length > 0) {
        return `${base64utf8String(name)} <${email}>`
      }
      return email
    })
    .filter((v): v is string => v != null)
    .join(', ')

  if (value.length === 0) {
    return []
  }
  return [{ name: 'Reply-To', value }]
}
